import { conditionalProbe } from './conditionalProbe';
/**
 * Navigation Management API Client
 *
 * Handles CRUD, draft/publish lifecycle, and runtime manifest
 * for backend-driven dynamic navigation trees.
 *
 * Follows the same 404-fallback pattern as RouteManagementApi
 * so the frontend can proceed before backend endpoints are available.
 */

import axios from 'axios';
import type { AxiosError } from 'axios';
import { buildSaasHeaders, resolveSaasContext } from '@/uptk/core/api/saasContext';
import type {
  NavigationDraftSnapshot,
  NavigationDraftUpdateRequest,
  NavigationModuleSetResponse,
  NavigationNode,
  NavigationNodeInput,
  NavigationPortalType,
  NavigationPublishRequest,
  NavigationPublishResponse,
  NavigationValidationResponse,
  RuntimeNavigationManifestResponse,
  RuntimeNavigationVersionResponse,
  GenerateNavigationFromEntitiesRequest,
  GenerateNavigationFromEntitiesResponse,
  NavigationVersionSummary,
} from '@/types/navigation_management';

// ─── Options ───────────────────────────────────────────────────

type ApiOptions = {
  tenantId?: string;
  organizationId?: string;
  workspaceId?: string;
};

type RuntimeNavigationOptions = ApiOptions & {
  portalType: NavigationPortalType;
  year?: string | null;
};

// ─── Error Handling ────────────────────────────────────────────

type ErrorDetails = {
  timestamp?: string;
  message?: string;
  details?: string[];
  error?: string;
};

export class NavigationManagementApiError extends Error {
  status: number | null;
  details: string[];
  code: string | null;

  constructor(
    message: string,
    status: number | null,
    details: string[] = [],
    code: string | null = null
  ) {
    super(message);
    this.name = 'NavigationManagementApiError';
    this.status = status;
    this.details = details;
    this.code = code;
  }
}

const toApiError = (error: unknown, fallbackMessage: string) => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ErrorDetails>;
    const status = axiosError.response?.status ?? null;
    const payload = axiosError.response?.data;
    const details = Array.isArray(payload?.details)
      ? payload.details.filter((entry): entry is string => typeof entry === 'string')
      : [];
    const message = payload?.message ?? axiosError.message ?? fallbackMessage;
    const code = payload?.error ?? null;
    return new NavigationManagementApiError(message, status, details, code);
  }

  if (error instanceof Error) {
    return new NavigationManagementApiError(error.message, null);
  }

  return new NavigationManagementApiError(fallbackMessage, null);
};

// ─── Headers ───────────────────────────────────────────────────

const buildHeaders = (options: ApiOptions = {}) => {
  const context = resolveSaasContext({
    tenantId: options.tenantId,
    organizationId: options.organizationId,
    workspaceId: options.workspaceId,
  });
  return buildSaasHeaders(context);
};

// ─── Normalizers ───────────────────────────────────────────────

const toStringSafe = (value: unknown, fallback = ''): string => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return fallback;
};

const normalizeNode = (raw: unknown): NavigationNode => {
  const node = raw as Record<string, unknown>;
  return {
    id: toStringSafe(node.id),
    moduleCode: toStringSafe(node.moduleCode),
    portalType: (node.portalType as NavigationPortalType) ?? 'portal',
    kind: (node.kind as NavigationNode['kind']) ?? 'link',
    key: toStringSafe(node.key),
    label: typeof node.label === 'string' ? node.label : undefined,
    iconKey: typeof node.iconKey === 'string' ? node.iconKey : undefined,
    routeId: typeof node.routeId === 'string' ? node.routeId : undefined,
    routePath: typeof node.routePath === 'string' ? node.routePath : undefined,
    externalHref: typeof node.externalHref === 'string' ? node.externalHref : undefined,
    externalTarget: node.externalTarget === '_self' ? '_self' : '_blank',
    order: typeof node.order === 'number' ? node.order : 0,
    enabled: node.enabled !== false,
    showInNavigation: node.showInNavigation !== false,
    badge: node.badge as NavigationNode['badge'] ?? undefined,
    guards: Array.isArray(node.guards) ? node.guards as NavigationNode['guards'] : [],
    children: Array.isArray(node.children)
      ? (node.children as unknown[]).map(normalizeNode)
      : [],
    metadata: (node.metadata as Record<string, unknown>) ?? undefined,
  };
};

const normalizeDraftSnapshot = (
  raw: unknown,
  fallbackStatus: NavigationDraftSnapshot['status']
): NavigationDraftSnapshot => {
  const snapshot = raw as Record<string, unknown>;
  return {
    versionId: snapshot.versionId as string | null ?? null,
    version: typeof snapshot.version === 'number' ? snapshot.version : 0,
    checksum: snapshot.checksum as string | null ?? null,
    status: (snapshot.status as NavigationDraftSnapshot['status']) ?? fallbackStatus,
    nodes: Array.isArray(snapshot.nodes)
      ? (snapshot.nodes as unknown[]).map(normalizeNode)
      : [],
    updatedAt: snapshot.updatedAt as string ?? new Date().toISOString(),
    updatedBy: snapshot.updatedBy as string | null ?? null,
    message: snapshot.message as string | null ?? null,
  };
};

const normalizeVersionSummary = (raw: unknown): NavigationVersionSummary => {
  const entry = raw as Record<string, unknown>;
  return {
    id: toStringSafe(entry.id),
    version: typeof entry.version === 'number' ? entry.version : 0,
    status: (entry.status as NavigationVersionSummary['status']) ?? 'DRAFT',
    checksum: entry.checksum as string | null ?? null,
    createdAt: entry.createdAt as string ?? new Date().toISOString(),
    createdBy: entry.createdBy as string | null ?? null,
    message: entry.message as string | null ?? null,
  };
};

// ─── Endpoint Prefix Resolution ────────────────────────────────

const endpointPrefixCandidates = [
  '/api/v1/modules',
  '/api/v1/route-management/modules',
] as const;

let preferredPrefix: (typeof endpointPrefixCandidates)[number] | null = null;

const getPrefixCandidates = () => {
  if (!preferredPrefix) {
    return [...endpointPrefixCandidates];
  }
  return [
    preferredPrefix,
    ...endpointPrefixCandidates.filter((c) => c !== preferredPrefix),
  ];
};

const buildNavigationPath = (
  prefix: string,
  moduleCode: string,
  suffix = ''
) => `${prefix}/${encodeURIComponent(moduleCode)}/navigation${suffix}`;

const requestWithPrefixFallback = async <T>(
  moduleCode: string,
  suffix: string,
  execute: (path: string) => Promise<T>
): Promise<T> => {
  let lastNotFoundError: unknown = null;

  for (const prefix of getPrefixCandidates()) {
    const path = buildNavigationPath(prefix, moduleCode, suffix);
    try {
      const result = await execute(path);
      preferredPrefix = prefix;
      return result;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        lastNotFoundError = error;
        continue;
      }
      throw error;
    }
  }

  throw lastNotFoundError ?? new NavigationManagementApiError(
    'Failed to resolve navigation endpoint.',
    null
  );
};

// ─── Runtime Query Builder ─────────────────────────────────────

const VALID_NAVIGATION_PORTAL_TYPES = new Set<string>(['portal', 'adminportal', 'make']);

const buildRuntimeQuery = (options: RuntimeNavigationOptions) => {
  const context = resolveSaasContext({
    tenantId: options.tenantId,
    organizationId: options.organizationId,
    workspaceId: options.workspaceId,
  });
  const params = new URLSearchParams();
  // Clamp invalid portalType values (e.g. 'default' from legacy URLs) to 'portal'
  const portalType = VALID_NAVIGATION_PORTAL_TYPES.has(options.portalType)
    ? options.portalType
    : 'portal';
  params.set('portalType', portalType);
  params.set('workspaceId', options.workspaceId ?? context.workspaceId);
  if (options.year && options.year.trim().length > 0) {
    params.set('year', options.year.trim());
  }
  return params;
};

// ─── Public API ────────────────────────────────────────────────

/**
 * Get the full navigation set (draft + published + history) for a module.
 */
export async function getModuleNavigationSet(
  moduleCode: string,
  options: ApiOptions = {}
): Promise<NavigationModuleSetResponse | null> {
  try {
    const response = await requestWithPrefixFallback(
      moduleCode,
      '',
      (path) => axios.get<unknown>(path, { headers: buildHeaders(options) })
    );

    const data = response.data as Record<string, unknown>;
    const module = data.module as Record<string, unknown>;
    return {
      module: {
        code: toStringSafe(module?.code, moduleCode),
        name: typeof module?.name === 'string' ? module.name : undefined,
        domain: typeof module?.domain === 'string' ? module.domain : undefined,
      },
      draft: data.draft ? normalizeDraftSnapshot(data.draft, 'DRAFT') : null,
      published: data.published ? normalizeDraftSnapshot(data.published, 'PUBLISHED') : null,
      history: Array.isArray(data.history)
        ? (data.history as unknown[]).map(normalizeVersionSummary)
        : [],
    };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw toApiError(error, 'Failed to load module navigation.');
  }
}

/**
 * Update the navigation draft for a module.
 */
export async function updateModuleNavigationDraft(
  moduleCode: string,
  payload: NavigationDraftUpdateRequest,
  options: ApiOptions = {}
): Promise<NavigationDraftSnapshot> {
  try {
    const response = await requestWithPrefixFallback(
      moduleCode,
      '/draft',
      (path) => axios.put<unknown>(path, payload, { headers: buildHeaders(options) })
    );
    return normalizeDraftSnapshot(response.data, 'DRAFT');
  } catch (error) {
    throw toApiError(error, 'Failed to update navigation draft.');
  }
}

/**
 * Validate a navigation draft without saving it.
 */
export async function validateModuleNavigationDraft(
  moduleCode: string,
  payload: NavigationDraftUpdateRequest,
  options: ApiOptions = {}
): Promise<NavigationValidationResponse> {
  try {
    const response = await requestWithPrefixFallback(
      moduleCode,
      '/validate',
      (path) => axios.post<unknown>(path, payload, { headers: buildHeaders(options) })
    );
    const data = response.data as Record<string, unknown>;
    return {
      valid: data.valid === true,
      issues: Array.isArray(data.issues) ? data.issues as NavigationValidationResponse['issues'] : [],
      normalizedDraft: data.normalizedDraft
        ? normalizeDraftSnapshot(data.normalizedDraft, 'DRAFT')
        : null,
    };
  } catch (error) {
    throw toApiError(error, 'Failed to validate navigation draft.');
  }
}

/**
 * Publish the current navigation draft.
 */
export async function publishModuleNavigationDraft(
  moduleCode: string,
  payload: NavigationPublishRequest,
  options: ApiOptions = {}
): Promise<NavigationPublishResponse> {
  try {
    const response = await requestWithPrefixFallback(
      moduleCode,
      '/publish',
      (path) => axios.post<unknown>(path, payload, { headers: buildHeaders(options) })
    );
    const data = response.data as Record<string, unknown>;
    return {
      published: normalizeDraftSnapshot(data.published, 'PUBLISHED'),
      previousVersionId: data.previousVersionId as string | null ?? null,
      invalidated: data.invalidated === true,
    };
  } catch (error) {
    throw toApiError(error, 'Failed to publish navigation draft.');
  }
}

/**
 * Get navigation version history for a module.
 */
export async function getModuleNavigationHistory(
  moduleCode: string,
  options: ApiOptions = {}
): Promise<NavigationVersionSummary[]> {
  try {
    const response = await requestWithPrefixFallback(
      moduleCode,
      '/history',
      (path) => axios.get<unknown>(path, { headers: buildHeaders(options) })
    );
    if (!Array.isArray(response.data)) {
      return [];
    }
    return (response.data as unknown[]).map(normalizeVersionSummary);
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return [];
    }
    throw toApiError(error, 'Failed to load navigation history.');
  }
}

/**
 * Revert to a specific navigation version.
 */
export async function revertModuleNavigationVersion(
  moduleCode: string,
  versionId: string,
  options: ApiOptions = {}
): Promise<NavigationModuleSetResponse> {
  try {
    const response = await requestWithPrefixFallback(
      moduleCode,
      `/revert/${encodeURIComponent(versionId)}`,
      (path) => axios.post<unknown>(path, {}, { headers: buildHeaders(options) })
    );
    const data = response.data as Record<string, unknown>;
    const module = data.module as Record<string, unknown>;
    return {
      module: {
        code: toStringSafe(module?.code, moduleCode),
        name: typeof module?.name === 'string' ? module.name : undefined,
        domain: typeof module?.domain === 'string' ? module.domain : undefined,
      },
      draft: data.draft ? normalizeDraftSnapshot(data.draft, 'DRAFT') : null,
      published: data.published ? normalizeDraftSnapshot(data.published, 'PUBLISHED') : null,
      history: Array.isArray(data.history)
        ? (data.history as unknown[]).map(normalizeVersionSummary)
        : [],
    };
  } catch (error) {
    throw toApiError(error, 'Failed to revert navigation version.');
  }
}

/**
 * Auto-generate navigation nodes from entity definitions.
 */
export async function generateModuleNavigationFromEntities(
  moduleCode: string,
  payload: GenerateNavigationFromEntitiesRequest,
  options: ApiOptions = {}
): Promise<GenerateNavigationFromEntitiesResponse> {
  try {
    const response = await requestWithPrefixFallback(
      moduleCode,
      '/generate-from-entities',
      (path) => axios.post<unknown>(path, payload, { headers: buildHeaders(options) })
    );
    const data = response.data as Record<string, unknown>;
    return {
      moduleCode: toStringSafe(data.moduleCode, moduleCode),
      generatedNodeIds: Array.isArray(data.generatedNodeIds)
        ? data.generatedNodeIds as string[]
        : [],
      skippedEntityIds: Array.isArray(data.skippedEntityIds)
        ? data.skippedEntityIds as string[]
        : [],
      draftVersion: typeof data.draftVersion === 'number' ? data.draftVersion : 0,
      checksum: data.checksum as string | null ?? null,
      draft: data.draft ? normalizeDraftSnapshot(data.draft, 'DRAFT') : null,
    };
  } catch (error) {
    throw toApiError(error, 'Failed to generate navigation from entities.');
  }
}

/**
 * Get the runtime navigation manifest for a portal context.
 * This is the primary endpoint consumed by the header DynamicTopNav.
 * Returns nodes already filtered by module entitlements, permissions, and roles.
 */
/**
 * Mise en commun du manifeste (retour d'Olivier du 20.10 : « le menu Module met une éternité à s'afficher »). L'en-tête, les
 * outils vocaux, la recherche et le fil d'Ariane appellent chacun ce manifeste (≈ 1,2 s côté serveur) : une dizaine d'appels
 * concurrents au chargement d'une page. Les appels simultanés partagent la même requête, et la réponse est réutilisée
 * pendant RUNTIME_MANIFEST_TTL_MS (assez court pour qu'un changement de version, détecté par sondage, recharge vraiment).
 */
export const RUNTIME_MANIFEST_TTL_MS = 5_000;
const runtimeManifestShared = new Map<string, { at: number; promise: Promise<RuntimeNavigationManifestResponse | null> }>();

/** Oublie les manifestes partagés (tests, changement de locataire). */
export function resetRuntimeNavigationManifestCache(): void {
  runtimeManifestShared.clear();
}

export async function getRuntimeNavigationManifest(
  options: RuntimeNavigationOptions
): Promise<RuntimeNavigationManifestResponse | null> {
  const query = buildRuntimeQuery(options).toString();
  const headers = buildHeaders(options);
  const key = `${query}|${JSON.stringify(headers ?? {})}`;
  const now = Date.now();
  const shared = runtimeManifestShared.get(key);
  if (shared && now - shared.at < RUNTIME_MANIFEST_TTL_MS) {
    return shared.promise;
  }
  const promise = fetchRuntimeNavigationManifest(options, query, headers);
  runtimeManifestShared.set(key, { at: now, promise });
  // Un échec n'est pas mis en commun : l'appel suivant retente.
  promise.catch(() => {
    if (runtimeManifestShared.get(key)?.promise === promise) runtimeManifestShared.delete(key);
  });
  return promise;
}

async function fetchRuntimeNavigationManifest(
  options: RuntimeNavigationOptions,
  query: string,
  headers: ReturnType<typeof buildHeaders>
): Promise<RuntimeNavigationManifestResponse | null> {
  try {
    const response = await axios.get<unknown>(
      `/api/v1/navigation/runtime?${query}`,
      { headers }
    );
    const data = response.data as Record<string, unknown>;
    const context = resolveSaasContext({
      tenantId: options.tenantId,
      organizationId: options.organizationId,
      workspaceId: options.workspaceId,
    });
    return {
      portalType: options.portalType,
      workspaceId: options.workspaceId ?? context.workspaceId,
      version: toStringSafe(data.version),
      checksum: data.checksum as string | null ?? null,
      generatedAt: data.generatedAt as string ?? new Date().toISOString(),
      nodes: Array.isArray(data.nodes)
        ? (data.nodes as unknown[]).map(normalizeNode)
        : [],
    };
  } catch (error) {
    if (axios.isAxiosError(error) && (error.response?.status === 404 || error.response?.status === 400)) {
      return null;
    }
    throw toApiError(error, 'Failed to load runtime navigation manifest.');
  }
}

/**
 * Poll the runtime navigation version for cache invalidation.
 */
export async function getRuntimeNavigationVersion(
  options: RuntimeNavigationOptions
): Promise<RuntimeNavigationVersionResponse | null> {
  try {
    const data = await conditionalProbe(
      `/api/v1/navigation/runtime/version?${buildRuntimeQuery(options).toString()}`,
      buildHeaders(options),
    ) as Record<string, unknown>;
    const context = resolveSaasContext({
      tenantId: options.tenantId,
      organizationId: options.organizationId,
      workspaceId: options.workspaceId,
    });
    return {
      portalType: options.portalType,
      workspaceId: options.workspaceId ?? context.workspaceId,
      version: toStringSafe(data.version),
      checksum: data.checksum as string | null ?? null,
    };
  } catch (error) {
    if (axios.isAxiosError(error) && (error.response?.status === 404 || error.response?.status === 400)) {
      return null;
    }
    throw toApiError(error, 'Failed to load runtime navigation version.');
  }
}

// ─── Local Draft Storage (404 Fallback) ────────────────────────

const localDraftStorageKey = 'navigation_draft_';

const getLocalDraftKey = (moduleCode: string) =>
  `${localDraftStorageKey}${moduleCode}`;

export const saveLocalNavigationDraft = (
  moduleCode: string,
  snapshot: NavigationDraftSnapshot
) => {
  if (typeof window === 'undefined') {
    return;
  }
  localStorage.setItem(getLocalDraftKey(moduleCode), JSON.stringify(snapshot));
};

export const loadLocalNavigationDraft = (
  moduleCode: string
): NavigationDraftSnapshot | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  const raw = localStorage.getItem(getLocalDraftKey(moduleCode));
  if (!raw) {
    return null;
  }
  try {
    return normalizeDraftSnapshot(JSON.parse(raw), 'DRAFT');
  } catch {
    return null;
  }
};

export const clearLocalNavigationDraft = (moduleCode: string) => {
  if (typeof window === 'undefined') {
    return;
  }
  localStorage.removeItem(getLocalDraftKey(moduleCode));
};

// ─── Local Runtime Manifest (404 Fallback) ─────────────────────

const localRuntimeManifestKey = 'navigation_runtime_manifest';

type LocalRuntimeManifestEntry = {
  portalType: NavigationPortalType;
  workspaceId: string;
  nodes: NavigationNode[];
  version: string;
  checksum: string | null;
  generatedAt: string;
};

export const saveLocalRuntimeManifest = (entries: LocalRuntimeManifestEntry[]) => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(localRuntimeManifestKey, JSON.stringify(entries));
};

export const loadLocalRuntimeManifest = (portalType: NavigationPortalType, workspaceId: string): LocalRuntimeManifestEntry | null => {
  if (typeof window === 'undefined') return null;
  const raw = localStorage.getItem(localRuntimeManifestKey);
  if (!raw) return null;
  try {
    const entries = JSON.parse(raw) as LocalRuntimeManifestEntry[];
    const entry = entries.find(
      (e) => e.portalType === portalType && (e.workspaceId === workspaceId || e.workspaceId === '*')
    );
    if (!entry) return null;
    return {
      ...entry,
      nodes: entry.nodes.map(normalizeNode),
    };
  } catch {
    return null;
  }
};

/**
 * Rebuild the local runtime manifest from all locally published navigation drafts.
 * Called when a draft is published locally (no backend).
 */

/**
 * Clear the local runtime manifest cache. Called on app mount so a stale
 * cache from a previous session doesn't mask a recovered/updated backend.
 * The backend is always the source of truth; the local cache is only a
 * transient fallback during an active outage.
 */
export const clearLocalRuntimeManifest = (): void => {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(localRuntimeManifestKey);
};

export const rebuildLocalRuntimeManifest = (workspaceId = '*') => {
  if (typeof window === 'undefined') return;

  const entries: LocalRuntimeManifestEntry[] = [];
  const portalTypes: NavigationPortalType[] = ['portal', 'adminportal', 'make'];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key || !key.startsWith(localDraftStorageKey)) continue;

    const raw = localStorage.getItem(key);
    if (!raw) continue;

    try {
      const snapshot = JSON.parse(raw) as NavigationDraftSnapshot;
      // Only include published snapshots
      if (snapshot.status !== 'PUBLISHED') continue;
      if (!Array.isArray(snapshot.nodes) || snapshot.nodes.length === 0) continue;

      for (const pt of portalTypes) {
        const matchingNodes = snapshot.nodes
          .filter((n) => n.portalType === pt)
          .map(normalizeNode);

        if (matchingNodes.length === 0) continue;

        // Merge with existing entry for this portal+workspace
        const existing = entries.find(
          (e) => e.portalType === pt && e.workspaceId === workspaceId
        );
        if (existing) {
          existing.nodes = [...existing.nodes, ...matchingNodes];
        } else {
          entries.push({
            portalType: pt,
            workspaceId,
            nodes: matchingNodes,
            version: `local:${snapshot.version}:${Date.now()}`,
            checksum: snapshot.checksum,
            generatedAt: new Date().toISOString(),
          });
        }
      }
    } catch {
      continue;
    }
  }

  // Sort nodes within each entry
  for (const entry of entries) {
    entry.nodes.sort((a, b) => a.order - b.order);
  }

  saveLocalRuntimeManifest(entries);
};

// ─── Navigation Manifest Invalidation Event ────────────────────

export const runtimeNavigationManifestInvalidatedEventName =
  'geode:runtime-navigation-manifest-invalidated';

export const dispatchRuntimeNavigationManifestInvalidated = (
  detail?: Record<string, unknown>
) => {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(
    new CustomEvent(runtimeNavigationManifestInvalidatedEventName, { detail })
  );
};
